import { useOpsStore } from '../../store/ops.store';
import type { RatingDistribution } from '../../types/ops';
import { Star, Download } from 'lucide-react';

function starCellClass(starLevel: number, isDark: boolean): string {
  if (starLevel <= 2) return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-600';
  if (starLevel >= 4) return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-600';
  return '';
}

export default function RatingDistributionTable({ isDark }: { isDark: boolean }) {
  const ratingDistribution = useOpsStore((s) => s.ratingDistribution);

  if (ratingDistribution.length === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-white text-gray-500 border border-gray-200 shadow-sm'}`}>
        Chưa có dữ liệu phân bố rating.
      </div>
    );
  }

  const exportCsv = () => {
    const header = ['Chương trình', '1 sao', '2 sao', '3 sao', '4 sao', '5 sao', 'Tổng', 'Trung bình'];
    const csvRows = [header.join(',')];
    for (const row of ratingDistribution) {
      csvRows.push([
        `"${row.programName.replace(/"/g, '""')}"`,
        row.star1, row.star2, row.star3, row.star4, row.star5,
        row.total, row.average.toFixed(2),
      ].join(','));
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rating_distribution.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <Star className="h-5 w-5 text-yellow-400" />
        <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Số lượng rating theo từng loại và từng bộ môn
        </h3>
        <div className="flex-1" />
        <button
          onClick={exportCsv}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className={`border-b ${isDark ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'}`}>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium">Chương trình</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">1 sao</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">2 sao</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">3 sao</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">4 sao</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">5 sao</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">Tổng</th>
              <th className="px-2 sm:px-4 py-2.5 sm:py-3 font-medium text-center">Trung bình</th>
            </tr>
          </thead>
          <tbody>
            {ratingDistribution.map((row: RatingDistribution, idx: number) => (
              <tr
                key={row.programName}
                className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'} ${
                  idx % 2 === 0
                    ? isDark ? 'bg-slate-800' : 'bg-white'
                    : isDark ? 'bg-slate-900' : 'bg-gray-50'
                } ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'} transition-colors`}
              >
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 font-medium whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {row.programName}
                </td>
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-center ${starCellClass(1, isDark)}`}>
                  {row.star1}
                </td>
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-center ${starCellClass(2, isDark)}`}>
                  {row.star2}
                </td>
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-center ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {row.star3}
                </td>
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-center ${starCellClass(4, isDark)}`}>
                  {row.star4}
                </td>
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-center ${starCellClass(5, isDark)}`}>
                  {row.star5}
                </td>
                <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-center font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {row.total}
                </td>
                <td className="px-2 sm:px-4 py-2.5 sm:py-3 text-center font-semibold text-yellow-400">
                  {row.average.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
